/**
 * Specialization slug ↔ display label mapping (provider profile / list UI).
 * For search filter label → API business_type key, use careTypeMappings instead.
 */

import {
  REVERSE_SPECIALIZATION_MAPPINGS,
  SPECIALIZATION_NAME_MAPPINGS,
  SPECIALIZATION_OPTIONS,
} from "../generated/careTaxonomy";
import {
  mapCareTypeToBusinessTypeKey,
  mapSpecializationToDatabase,
} from "./careTypeMappings";

export { SPECIALIZATION_NAME_MAPPINGS };
export { REVERSE_SPECIALIZATION_MAPPINGS } from "../generated/careTaxonomy";
export {
  mapCareTypeToBusinessTypeKey,
  mapSpecializationToDatabase,
};

type DatabaseSpecialization = keyof typeof SPECIALIZATION_NAME_MAPPINGS;
type DisplaySpecialization =
  (typeof SPECIALIZATION_NAME_MAPPINGS)[DatabaseSpecialization];

export const mapSpecializationToDisplay = (
  specializationName: string
): DisplaySpecialization | string => {
  if (!specializationName || typeof specializationName !== "string") {
    return "";
  }
  return SPECIALIZATION_NAME_MAPPINGS[specializationName] || specializationName;
};

export const mapSpecializationsToDisplay = (
  specializations: string[]
): DisplaySpecialization[] => {
  if (!Array.isArray(specializations)) {
    return [];
  }
  return specializations.map(
    mapSpecializationToDisplay
  ) as DisplaySpecialization[];
};

export const getDatabaseSpecializationNames = (): DatabaseSpecialization[] => {
  return Object.keys(SPECIALIZATION_NAME_MAPPINGS) as DatabaseSpecialization[];
};

export const getDisplaySpecializationNames = (): DisplaySpecialization[] => {
  return Array.from(
    new Set(Object.values(SPECIALIZATION_NAME_MAPPINGS))
  ) as DisplaySpecialization[];
};

export const parseAndMapSpecializations = (
  specializationsField: string | string[] | undefined
): DisplaySpecialization[] => {
  if (!specializationsField) return [];

  let specializations: string[] = [];

  if (Array.isArray(specializationsField)) {
    specializations = specializationsField;
  } else if (typeof specializationsField === "string") {
    try {
      const parsed = JSON.parse(specializationsField);
      if (Array.isArray(parsed)) {
        specializations = parsed.filter(
          (value): value is string => typeof value === "string"
        );
      } else if (typeof parsed === "string") {
        specializations = [parsed];
      }
    } catch {
      specializations = specializationsField
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
  }

  const mappedSpecializations = mapSpecializationsToDisplay(specializations);
  return Array.from(new Set(mappedSpecializations));
};

export const getUnknownSpecializations = (
  specializations: string[] | undefined
): string[] => {
  if (!Array.isArray(specializations)) return [];
  const knownValues = new Set(
    [
      ...SPECIALIZATION_OPTIONS.flatMap((option) => [
        option.key,
        option.label,
      ]),
      ...Object.keys(SPECIALIZATION_NAME_MAPPINGS),
      ...Object.keys(REVERSE_SPECIALIZATION_MAPPINGS),
    ].map((value) => value.trim().toLocaleLowerCase())
  );
  return Array.from(
    new Set(
      specializations
        .map((value) => value.trim())
        .filter(
          (value) =>
            value.length > 0 &&
            !knownValues.has(value.toLocaleLowerCase())
        )
    )
  );
};
